<template>
  <v-dialog
    :value="value"
    fullscreen
    persistent
    transition="dialog-bottom-transition"
    @input="$emit('input', $event)"
  >
    <v-card class="preview-dialog-card">
      <div class="preview-banner">
        <v-icon class="preview-banner__icon">mdi-eye</v-icon>
        <span class="preview-banner__title">Preview Mode</span>
        <v-spacer></v-spacer>
        <v-btn icon class="preview-close-btn" data-testid="preview-close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <Alert
        full-width
        variant="warning"
        message="String values may be missing if aggregate metrics are selected"
      />
      <div v-if="showViewTabs" class="view-switcher">
        <v-tabs
          v-model="activeView"
          centered
          background-color="transparent"
          color="brandPrimary"
          slider-color="brandPrimary"
        >
          <v-tab data-testid="preview-view-tab">Summary Card</v-tab>
          <v-tab data-testid="preview-view-tab">Detail Page</v-tab>
        </v-tabs>
      </div>

      <div v-if="showingSummary" class="summary-view">
        <ProgramSummaryCard
          class="summary-view__card"
          :program="summaryProgram"
          :earnings="earnings"
          :basis="basis"
          preview
        />
      </div>

      <DynamicProgramDetailPage
        v-if="showingDetail"
        :program-name="programName"
        :image-url="imagePlaceholder"
        :date-range-label="dateRangeLabel"
        :metric-rows="metricRows"
        :basis="basis"
        :is-dollar-budget="isDollarBudget"
        :tabs="tabItems"
        :active-tab-key="activeTabKey"
        :show-submit-claim="isClaimProgram"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import ProgramSummaryCard from "@/components/participant/programs/ProgramSummaryCard.vue";
import DynamicProgramDetailPage from "@/components/participant/programs/DynamicProgramDetailPage.vue";
import Alert from "@/components/generic/Alert.vue";

export default {
  name: "DynamicProgramPreview",
  components: { ProgramSummaryCard, DynamicProgramDetailPage, Alert },
  props: {
    value: { type: Boolean, default: false },
    programName: { type: String, default: "It's Sales Data" },
    dateRangeLabel: { type: String, default: "Jan 26, 2026 – Apr 26, 2026" },
    imagePlaceholder: {
      type: String,
      default: null,
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    activeTabKey: { type: [String, Number], default: null },
    metricRows: { type: Array, default: () => [] },
    basis: { type: String, default: null },
    isDollarBudget: { type: Boolean, default: false },
    showSummaryCard: { type: Boolean, default: false },
    showDetailPage: { type: Boolean, default: true },
    startDate: { type: String, default: null },
    endDate: { type: String, default: null },
    earnings: { type: [String, Number], default: 0 },
    isClaimProgram: { type: Boolean, default: false },
  },
  data() {
    return {
      activeView: 0,
    };
  },
  computed: {
    tabItems() {
      return this.tabs;
    },
    showViewTabs() {
      return this.showSummaryCard && this.showDetailPage;
    },
    showingSummary() {
      if (!this.showSummaryCard) return false;
      return !this.showViewTabs || this.activeView === 0;
    },
    showingDetail() {
      if (!this.showDetailPage) return false;
      return !this.showViewTabs || this.activeView === 1;
    },
    summaryProgram() {
      return {
        displayName: this.programName,
        name: this.programName,
        startDate: this.startDate,
        endDate: this.endDate,
        imageURL: this.imagePlaceholder,
        awardType: this.isDollarBudget ? "cash" : "points",
        type: "dynamic-data",
      };
    },
  },
  watch: {
    // Reset to the summary card only when the dialog opens.
    value(next) {
      if (next) this.activeView = 0;
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.preview-dialog-card {
  background: #eeeeee !important;
  color: rgba(0, 0, 0, 1) !important;
}

.preview-banner {
  background: #ffc107;
  padding: 12px 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
}

.preview-banner__icon {
  width: 24px;
  height: 24px;
  color: rgba(0, 0, 0, 0.87) !important;
}

.preview-banner__title {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: rgba(0, 0, 0, 0.87);
}

.preview-close-btn {
  width: 40px;
  height: 40px;
  color: rgba(0, 0, 0, 0.87) !important;
}

.view-switcher {
  display: flex;
  justify-content: center;
  padding-top: 24px;
}

.view-switcher ::v-deep .v-tabs {
  flex: 0 1 auto;
  width: auto;
}

.view-switcher ::v-deep .v-slide-group__content {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.summary-view {
  padding: 16px 29px 40px;
}

.summary-view__card {
  border-top: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>
